import { useState, useCallback, useEffect } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { getPortalCredentials, createOrUpdatePortalCredential, deletePortalCredential } from "../api/api";

const usePortalCredentials = () => {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const fetchCredentials = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getPortalCredentials();
      setCredentials(response.data?.credentials || []);
    } catch (err) {
      const errorMessage = err.response?.data?.error || t("portal_credentials.error_loading");
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const saveCredential = useCallback(
    async (portalId, username, password) => {
      setIsSaving(true);
      setError(null);
      try {
        const response = await createOrUpdatePortalCredential(portalId, username, password);
        message.success(response.data?.message || t("portal_credentials.save_success"));
        await fetchCredentials();
        return { success: true, credential: response.data?.credential };
      } catch (err) {
        const errorMessage = err.response?.data?.error || t("portal_credentials.error_saving");
        setError(errorMessage);
        message.error(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsSaving(false);
      }
    },
    [fetchCredentials, t]
  );

  const removeCredential = useCallback(
    async (credentialId) => {
      setDeletingId(credentialId);
      setError(null);
      try {
        await deletePortalCredential(credentialId);
        message.success(t("portal_credentials.delete_success"));
        await fetchCredentials();
        return { success: true };
      } catch (err) {
        const errorMessage = err.response?.data?.error || t("portal_credentials.error_deleting");
        setError(errorMessage);
        message.error(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setDeletingId(null);
      }
    },
    [fetchCredentials, t]
  );

  const getCredentialForPortal = useCallback(
    (portalId) => {
      return credentials.find((c) => c.portalId === portalId);
    },
    [credentials]
  );

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  return {
    credentials,
    isLoading,
    isSaving,
    deletingId,
    error,
    fetchCredentials,
    saveCredential,
    removeCredential,
    getCredentialForPortal
  };
};

export { usePortalCredentials };
